import React, { useState, useEffect, useContext } from 'react';
import { ArrowLeftOutlined, EyeOutlined, FilterOutlined } from '@ant-design/icons';
import { useHistory } from 'react-router-dom';
import { Table, Select, DatePicker, Input, Button, Card, Tag, Drawer, Typography, Space } from 'antd';
import moment from 'moment';
import { AppContext } from '../../../context/AppContext';
import API from '../../../API';
import * as AppPermissions from '../../../Util/permissions';

const { Option } = Select;
const { RangePicker } = DatePicker;
const { Text, Paragraph } = Typography;

const AuditLogs = () => {
    const history = useHistory();
    const { permissions } = useContext(AppContext);
    
    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(false);
    const [pagination, setPagination] = useState({ current: 1, pageSize: 20, total: 0 });
    const [filters, setFilters] = useState({
        log_name: '',
        causer_id: '',
        subject_type: '',
        description: '',
        date_from: '',
        date_to: ''
    });
    const [selectedLog, setSelectedLog] = useState(null);
    const [drawerVisible, setDrawerVisible] = useState(false);

    useEffect(() => {
        if (!permissions.includes(AppPermissions.CAN_READ_AUDIT_LOG)) return;
        let mounted = true;
        (async () => {
            setLoading(true);
            try {
                const params = {
                    page: pagination.current,
                    per_page: pagination.pageSize,
                    ...filters,
                };

                Object.keys(params).forEach((key) => {
                    if (!params[key]) delete params[key];
                });

                const response = await API.getActivityLogs(params);
                const pageData = response?.data; 
                const rows = Array.isArray(pageData?.data) ? pageData.data : [];

                if (!mounted) return;
                setLogs(rows);
                setPagination((prev) => {
                    const next = {
                        ...prev,
                        total: pageData?.total ?? prev.total,
                        current: pageData?.current_page ?? prev.current,
                        pageSize: pageData?.per_page ? Number(pageData.per_page) : prev.pageSize,
                    };
                    if (
                        next.total === prev.total &&
                        next.current === prev.current &&
                        next.pageSize === prev.pageSize
                    ) {
                        return prev;
                    }
                    return next;
                });
            } catch (error) {
                if (mounted) console.error('Failed to load activity logs:', error);
            } finally {
                if (mounted) setLoading(false);
            }
        })();
        return () => {
            mounted = false;
        };
        // Re-run when perms, page, pageSize, or filters change
    }, [permissions, pagination, filters]);

    const handleBack = () => {
        history.goBack();
    };

    const handleFilter = () => {
        setPagination(prev => ({ ...prev, current: 1 }));
    };

    const handleReset = () => {
        setFilters({
            log_name: '',
            causer_id: '',
            subject_type: '',
            description: '',
            date_from: '',
            date_to: ''
        });
    setPagination(prev => ({ ...prev, current: 1 }));
    };

    const handleViewDetails = (log) => {
        setSelectedLog(log);
        setDrawerVisible(true);
    };

    const handleTableChange = (pg) => {
        setPagination(prev => {
            const next = { ...prev, current: pg.current, pageSize: pg.pageSize };
            if (next.current === prev.current && next.pageSize === prev.pageSize) return prev;
            return next;
        });
    };

    const getLogLevelColor = (logName) => {
        const colors = {
            'security': 'red',
            'access_control': 'orange',
            'model': 'blue',
            'custom': 'green'
        };
        return colors[logName] || 'default';
    };

    const columns = [
        {
            title: 'Category',
            dataIndex: 'log_name',
            key: 'log_name',
            render: (logName) => (
                <Tag color={getLogLevelColor(logName)}>
                    {logName?.replace('_', ' ').toUpperCase()}
                </Tag>
            ),
            filters: [
                { text: 'Security', value: 'security' },
                { text: 'Access Control', value: 'access_control' },
                { text: 'Model', value: 'model' },
                { text: 'Custom', value: 'custom' }
            ],
            filterMultiple: false,
        },
        {
            title: 'Description',
            dataIndex: 'description',
            key: 'description',
            ellipsis: true,
        },
        {
            title: 'User',
            dataIndex: ['causer', 'first_name'],
            key: 'user',
            render: (firstName, record) => (
                record.causer ? `${firstName} ${record.causer.last_name}` : 'System'
            ),
        },
        {
            title: 'Date & Time',
            dataIndex: 'created_at',
            key: 'created_at',
            render: (date) => moment(date).format('YYYY-MM-DD HH:mm:ss'),
            sorter: true,
        },
        {
            title: 'Actions',
            key: 'actions',
            render: (_, record) => (
                <Button
                    type="link"
                    icon={<EyeOutlined />}
                    onClick={() => handleViewDetails(record)}
                >
                    View Details
                </Button>
            ),
        }
    ];

    if (!permissions.includes(AppPermissions.CAN_READ_AUDIT_LOG)) {
        return (
            <div className="audit-logs-container" style={{ padding: '16px' }}>
                <div className="audit-logs-header">
                    <button onClick={handleBack} className="ActionBar-backButton">
                        <ArrowLeftOutlined />
                        <span className="ActionBar-backButton-text">Back</span>
                    </button>
                </div>
                <div style={{ textAlign: 'center', padding: '50px' }}>
                    <Text type="secondary">
                        You don't have permission to view audit logs.
                    </Text>
                </div>
            </div>
        );
    }

    return (
        <div className="audit-logs-container" style={{ padding: "16px" }}>
            <div className="audit-logs-header">
                <button onClick={handleBack} className="ActionBar-backButton">
                    <ArrowLeftOutlined />
                    <span className="ActionBar-backButton-text">Back</span>
                </button>
            </div>

            <div className="audit-logs-content" style={{ padding: '20px' }}>
                <h1 className="section-title">Audit Logs</h1>
                <p className="section-subtitle">
                    View and monitor system activities and user actions
                </p>

                <Card title={<><FilterOutlined /> Filters</>} style={{ marginBottom: '20px' }}>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px' }}>
                        <Select
                            placeholder="Category"
                            value={filters.log_name}
                            onChange={(value) => setFilters({ ...filters, log_name: value })}
                            allowClear
                        >
                            <Option value="security">Security</Option>
                            <Option value="access_control">Access Control</Option>
                            <Option value="model">Model Changes</Option>
                            <Option value="custom">Custom</Option>
                        </Select>

                        <Input
                            placeholder="Search description"
                            value={filters.description}
                            onChange={(e) => setFilters({ ...filters, description: e.target.value })}
                        />

                        <RangePicker
                            placeholder={['From Date', 'To Date']}
                            onChange={(dates) => {
                                if (dates) {
                                    setFilters({
                                        ...filters,
                                        date_from: dates[0].format('YYYY-MM-DD'),
                                        date_to: dates[1].format('YYYY-MM-DD')
                                    });
                                } else {
                                    setFilters({ ...filters, date_from: '', date_to: '' });
                                }
                            }}
                        />
                    </div>

                    <div style={{ marginTop: '16px', textAlign: 'right' }}>
                        <Space>
                            <Button onClick={handleReset}>Reset</Button>
                            <Button type="primary" onClick={handleFilter}>Apply Filters</Button>
                        </Space>
                    </div>
                </Card>

                <Table
                    columns={columns}
                    dataSource={Array.isArray(logs) ? logs : []}
                    loading={loading}
                    pagination={{
                        ...pagination,
                        showSizeChanger: true,
                        showQuickJumper: true,
                        showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
                    }}
                    onChange={handleTableChange}
                    rowKey="id"
                />

                <Drawer
                    title="Activity Log Details"
                    placement="right"
                    onClose={() => setDrawerVisible(false)}
                    visible={drawerVisible}
                    width={600}
                >
                    {selectedLog && (
                        <div>
                            <div style={{ marginBottom: '16px' }}>
                                <Text strong>Category: </Text>
                                <Tag color={getLogLevelColor(selectedLog.log_name)}>
                                    {selectedLog.log_name?.replace('_', ' ').toUpperCase()}
                                </Tag>
                            </div>

                            <div style={{ marginBottom: '16px' }}>
                                <Text strong>Description: </Text>
                                <Paragraph>{selectedLog.description}</Paragraph>
                            </div>

                            <div style={{ marginBottom: '16px' }}>
                                <Text strong>User: </Text>
                                <Text>
                                    {selectedLog.causer 
                                        ? `${selectedLog.causer.first_name} ${selectedLog.causer.last_name}` 
                                        : 'System'}
                                </Text>
                            </div>

                            <div style={{ marginBottom: '16px' }}>
                                <Text strong>Date & Time: </Text>
                                <Text>{moment(selectedLog.created_at).format('YYYY-MM-DD HH:mm:ss')}</Text>
                            </div>

                            {selectedLog.subject_type && (
                                <div style={{ marginBottom: '16px' }}>
                                    <Text strong>Subject Type: </Text>
                                    <Text>{selectedLog.subject_type}</Text>
                                </div>
                            )}

                            {selectedLog.subject_id && (
                                <div style={{ marginBottom: '16px' }}>
                                    <Text strong>Subject ID: </Text>
                                    <Text>{selectedLog.subject_id}</Text>
                                </div>
                            )}

                            {selectedLog.properties && Object.keys(selectedLog.properties).length > 0 && (
                                <div>
                                    <Text strong>Additional Details: </Text>
                                    <pre style={{ 
                                        background: '#f5f5f5', 
                                        padding: '12px', 
                                        borderRadius: '4px',
                                        fontSize: '12px',
                                        overflow: 'auto',
                                        maxHeight: '300px'
                                    }}>
                                        {JSON.stringify(selectedLog.properties, null, 2)}
                                    </pre>
                                </div>
                            )}
                        </div>
                    )}
                </Drawer>
            </div>
        </div>
    );
};

export default AuditLogs;